import React from 'react';
import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import {
    DialogContent,
    Box,
    Typography,
    IconButton,
    Button,
    Tooltip,
    TextField,
    LinearProgress,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Link,
} from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import MinimizeIcon from '@material-ui/icons/Minimize';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SendIcon from '@material-ui/icons/Send';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import GavelIcon from '@material-ui/icons/Gavel';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import RefreshIcon from '@material-ui/icons/Refresh';
import VolumeUpOutlinedIcon from '@material-ui/icons/VolumeUpOutlined';
import StopIcon from '@material-ui/icons/Stop';
import { WandIcon } from '../components/icons/WandIcon';
import { AnimatedItem } from '../components/animations/AnimatedItem';
import { SkeletonLoader } from '../components/loading/SkeletonLoader';
import { browserHistory, requestSupportWithNote } from '../components/AppContent';
import { linkToPage } from '../App';
import { DocumentAiSession, useAiDocumentChat, getDocumentAiDisplayState } from './AiDocumentChatContext';
import { MarkdownText, renderInline } from './MarkdownText';
import { EnquiryAssessmentProcessingStatus } from './DocumentAiRepository';
import { AI_ACCENT as ACCENT, AI_ACCENT_SOFT as ACCENT_SOFT } from './aiTheme';

// The enquiry assessment's own coarse steps, in order - Ready/Failed are terminal outcomes
// (shown via the ready/failed display states elsewhere), not steps in this sequence. This is a
// distinct 5-value status from the per-document pipeline (see EnquiryAssessmentProcessingStatus's
// own comment) - a reassessment still fans out through per-document processing internally, but
// the UI never shows that breakdown, only "processing documents" vs. "preparing combined
// assessment".
const AI_PROCESSING_STEPS: { status: EnquiryAssessmentProcessingStatus; label: string; description: string }[] = [
    { status: EnquiryAssessmentProcessingStatus.Queued, label: 'Queued', description: 'Waiting in line for a worker to pick this run up.' },
    { status: EnquiryAssessmentProcessingStatus.ProcessingDocuments, label: 'Processing documents', description: 'Making sure every attached document has a current, saved finding.' },
    { status: EnquiryAssessmentProcessingStatus.Assessing, label: 'Preparing combined assessment', description: 'Combining the enquiry details and saved document findings into one assessment.' },
];

// Heading+paragraph groups for the processing skeleton - stand in for the Markdown headings/body
// text the real analysis will render as, filling the visible area instead of one small boxed
// placeholder (explicit bug report - see its own comment where it's used).
const ANALYSIS_SKELETON_GROUPS: { headingWidth: string; bodyRows: number }[] = [
    { headingWidth: '60%', bodyRows: 4 },
    { headingWidth: '45%', bodyRows: 3 },
    { headingWidth: '50%', bodyRows: 5 },
];

// The model's own combined-assessment JSON (EntityAIAssessment.AssessmentJson) - a fixed set of
// keys the current prompt requests, but still model-generated, not a guaranteed rigid shape (any
// key can be missing, or hold a string/array/object interchangeably), so this is parsed and
// rendered defensively rather than trusting any one shape.
interface ParsedAiAssessment {
    [sectionKey: string]: unknown;
}

const parseAssessmentJson = (rawJson: string | null): ParsedAiAssessment | null => {
    if (!rawJson) {
        return null;
    }
    try {
        const parsed = JSON.parse(rawJson);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as ParsedAiAssessment : null;
    // tslint:disable-next-line: no-empty
    } catch {
        return null;
    }
};

const isSectionEmpty = (value: unknown): boolean => {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim().length === 0;
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (typeof value === 'object') {
        return Object.keys(value as object).length === 0;
    }
    return false;
};

// Converts whatever shape a section's value happens to be into Markdown text MarkdownText.tsx can
// render consistently (bullets/bold) - the model's own JSON mixes plain strings, arrays and
// nested objects across enquiries and even across keys in the same assessment.
const stringifySectionValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return value.map(item => `- ${stringifySectionValue(item).replace(/\r?\n/g, ' ')}`).join('\n');
    }
    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>)
            .map(([entryKey, entryValue]) => `- **${entryKey}:** ${stringifySectionValue(entryValue).replace(/\r?\n/g, ' ')}`)
            .join('\n');
    }
    return String(value);
};

// Strips the backend's own **bold**/*italic*/_italic_ emphasis markers rather than rendering
// them - for the urgentDates date badge specifically, a compact pill that already has its own
// colour/weight treatment (see urgentDateBadge's own style), where nesting MarkdownText's own
// entity-pill styling would just look like a pill inside a pill.
const stripMarkdownEmphasis = (value: unknown): string => (
    stringifySectionValue(value).replace(/\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_/g, (_match, bold, italic1, italic2) => bold ?? italic1 ?? italic2 ?? '')
);

// Deliberately excludes two keys, each handled separately below instead of as one of these
// interchangeable section cards:
// - "citations" - the model's own free-text source pointers inside assessmentJson, not the
//   backend's verified citation records (citationsJson, rendered as its own "Sources" section -
//   see parseCitations below), so rendering this generic key would risk surfacing exactly the raw
//   chunk/GUID references the backend's own guidance says must never be shown as citations.
// - "urgentDates" - a structured {date, event, support} array, not a narrative string/array of
//   strings like the rest - rendered as its own date-badge layout instead of a generic dump (see
//   the dedicated block in the render below).
// - "disclaimer" - always shown, distinct styling, also not one of these cards.
//
// evidenceComplete's label is deliberately "All attached documents reviewed", not "Evidence
// complete" - per the backend's own guidance, this field describes processing coverage, not
// whether the solicitor has every piece of evidence the matter actually needs.
const ASSESSMENT_SECTIONS: { key: string; label: string; emphasis?: boolean }[] = [
    { key: 'executiveSummary', label: 'Executive summary' },
    { key: 'viabilityConsiderations', label: 'Viability considerations' },
    { key: 'keyFacts', label: 'Key facts' },
    { key: 'legalIssues', label: 'Legal issues' },
    { key: 'strengths', label: 'Strengths' },
    { key: 'risks', label: 'Risks', emphasis: true },
    { key: 'missingInformation', label: 'Missing information', emphasis: true },
    { key: 'questionsForClient', label: 'Questions for client' },
    { key: 'recommendedNextSteps', label: 'Recommended next steps' },
    { key: 'evidenceComplete', label: 'All attached documents reviewed' },
    { key: 'uncertainties', label: 'Uncertainties' },
];

/** One urgentDates entry - {date, event, support}, per the backend's own enquiry-assessment-v2
 * prompt. Still model-generated, so every field is read defensively rather than assumed present. */
interface ParsedUrgentDate {
    date?: unknown;
    event?: unknown;
    support?: unknown;
}

/** One citationsJson entry - backend-resolved (not raw model text), so documentName/
 * sourceReference are already customer-safe; documentGuid/processingGuid are kept only as a
 * React key, never shown. */
interface ParsedAssessmentCitation {
    documentName?: unknown;
    documentGuid?: unknown;
    processingGuid?: unknown;
    sourceReference?: unknown;
}

const parseCitationsJson = (rawJson: string | null): ParsedAssessmentCitation[] => {
    if (!rawJson) {
        return [];
    }
    try {
        const parsed = JSON.parse(rawJson);
        return Array.isArray(parsed) ? parsed as ParsedAssessmentCitation[] : [];
    // tslint:disable-next-line: no-empty
    } catch {
        return [];
    }
};

// Colour + structural language ported from taskflowBuilder/configureTaskDialog/ConfigureTaskDialog.tsx
// (solid accent header with eyebrow/title, sticky footer) - explicit request. Single-column
// here rather than that dialog's tabbed layout, since this isn't a multi-step wizard.

// Above EnquiryFormDrawer.tsx's own Drawer (zIndex: 1300 there) - explicit bug report: with the
// panel built on MUI's Dialog/Modal (even with hideBackdrop), opening the Enquiry drawer FROM
// this panel's own "Open enquiry" button re-triggered MUI's modal stacking manager, which bumped
// the more-recently-opened Drawer above this panel regardless of hideBackdrop, making the panel
// inaccessible behind it. A plain fixed-position Paper with its own explicit z-index, portalled
// straight to document.body (bypassing Dialog/Modal entirely), can't lose that tug-of-war.
const AI_PANEL_Z_INDEX = 1400;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // left avoids a centering transform on purpose - react-draggable owns this node's own
        // `transform` entirely (translate(dx, dy) from wherever it's dragged to), so centering
        // via transform: translateX(-50%) would get wiped out the moment Draggable mounts.
        panel: {
            position: 'fixed',
            top: '4vh',
            left: 'calc(50vw - min(550px, 46vw))',
            width: 'min(1100px, 92vw)',
            height: '92vh',
            maxHeight: 980,
            zIndex: AI_PANEL_Z_INDEX,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 4,
        },
        // Top-level, not nested inside stageStepper's own rule - JSS's nesting plugin can't
        // handle @keyframes declared inside an already-nested (&-selector) rule, it needs to sit
        // alongside the other top-level style keys (same as AiProcessingIndicator.tsx's own
        // keyframes) - explicit bug report/crash when this was nested: "container.addRule(...)
        // .addRule is not a function".
        '@keyframes aiConnectorFlow': {
            from: { backgroundPosition: '0 0' },
            to: { backgroundPosition: '32px 0' },
        },
        // Top-level, same reasoning as aiConnectorFlow above (JSS can't nest @keyframes inside an
        // &-selector rule). Explicit request: documentAIQuestion has no incremental progress to
        // show, unlike document processing's own Stepper - a "someone's typing" style indicator
        // is the equivalent feedback that the server is actually working on an answer.
        '@keyframes aiTypingBounce': {
            '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
            '30%': { transform: 'translateY(-4px)', opacity: 1 },
        },
        header: {
            background: ACCENT,
            color: '#fff',
            padding: theme.spacing(1.75, 2.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            minWidth: 0,
        },
        headerIconChip: {
            width: 34,
            height: 34,
            borderRadius: 10,
            backgroundColor: 'rgba(255,255,255,0.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: 19 },
        },
        headerText: {
            minWidth: 0,
        },
        headerEyebrow: {
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 500,
            color: '#fff',
            opacity: 0.85,
            marginBottom: 1,
        },
        headerTitle: {
            fontWeight: 600,
            fontSize: 16,
            color: '#fff',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        headerActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            flexShrink: 0,
        },
        headerIconBtn: {
            color: '#fff',
            padding: 6,
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.14)',
            },
        },
        // Explicit request: make this one stand out more than the minimize/close icon buttons
        // next to it - a proper pill with its own label, not just another small icon.
        openEnquiryBtn: {
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            borderRadius: 999,
            padding: theme.spacing(0.5, 1.75),
            fontSize: 12.5,
            fontWeight: 600,
            textTransform: 'none',
            marginRight: theme.spacing(0.75),
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.14)',
                borderColor: 'rgba(255,255,255,0.7)',
            },
        },
        // Explicit request: quickly identify which enquiry a chat belongs to (name/email/area
        // of law - the same fields EnquiryFormDrawer.tsx's own header shows), since this dialog
        // can stay open long after that drawer's own header is out of view/closed entirely.
        identityRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: theme.spacing(2),
            padding: theme.spacing(1, 2.5),
            backgroundColor: ACCENT_SOFT,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
        },
        identityItem: {
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 12.5,
            fontWeight: 600,
            color: ACCENT,
            minWidth: 0,
            '& .MuiSvgIcon-root': { fontSize: 15, flexShrink: 0 },
            '& > span': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
        body: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            padding: 0,
        },
        // Explicit bug report: a separately height-capped (42%) section left a huge empty gap
        // above the chat compose box whenever there were few/no questions yet. Now just a plain,
        // uncapped block that scrolls together with the chat messages below it inside chatArea
        // (see chatArea's own comment) - the analysis reads full width, not squeezed into its own
        // fixed-height pane.
        resultSection: {
            padding: theme.spacing(2.5, 2.5, 2, 2.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: '#fafafa',
        },
        resultSectionTitle: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(1),
        },
        // Explicit bug report: a single boxed/bordered "table" skeleton (variant="panel") read as
        // a data-grid placeholder, not a document loading, and only covered a small strip near
        // the top with a lot of dead white space below it. A few heading+paragraph groups (plain
        // SkeletonLoader variant="text", no Paper/border) stacked to fill the visible area instead
        // reads as the document body it's actually standing in for.
        analysisSkeletonGroup: {
            marginBottom: theme.spacing(2.5),
            '&:last-child': {
                marginBottom: 0,
            },
        },
        analysisSkeletonHeading: {
            marginBottom: theme.spacing(1),
        },
        // Explicit request: the pipeline stage read as a plain text label ("Extracting", etc) -
        // a Stepper reads as visibly advancing progress instead, which is what was actually
        // asked for. Colours overridden from MUI's own default primary-colour scheme to this
        // feature's own AI_ACCENT purple, same as everywhere else in this dialog.
        stageStepper: {
            padding: theme.spacing(0, 0, 2, 0),
            backgroundColor: 'transparent',
            '& .MuiStepIcon-root.MuiStepIcon-active, & .MuiStepIcon-root.MuiStepIcon-completed': {
                color: ACCENT,
            },
            '& .MuiStepConnector-line': {
                borderColor: theme.palette.divider,
            },
            '& .MuiStepConnector-completed .MuiStepConnector-line': {
                borderColor: ACCENT,
            },
            // The segment currently being traversed (the connector leading into whichever step
            // is active) - explicit request for a moving "progress" feel here specifically,
            // rather than just a static colour change like the completed segments get. A
            // marching dashed line reads as "flowing" without needing a real percentage for this
            // particular segment (unlike the LinearProgress bar below, which does have one).
            '& .MuiStepConnector-active .MuiStepConnector-line': {
                border: 'none',
                height: 2,
                backgroundImage: `repeating-linear-gradient(90deg, ${ACCENT} 0, ${ACCENT} 8px, transparent 8px, transparent 16px)`,
                backgroundSize: '32px 2px',
                animation: '$aiConnectorFlow 900ms linear infinite',
                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    backgroundImage: 'none',
                    border: `2px solid ${ACCENT}`,
                    height: 0,
                },
            },
            '& .MuiStepLabel-label.MuiStepLabel-active, & .MuiStepLabel-label.MuiStepLabel-completed': {
                color: ACCENT,
                fontWeight: 700,
            },
        },
        stageDetail: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginTop: 2,
        },
        progressStage: {
            fontSize: 12.5,
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(1),
        },
        progressBar: {
            marginBottom: theme.spacing(2),
            borderRadius: 4,
            height: 6,
            backgroundColor: ACCENT_SOFT,
            '& .MuiLinearProgress-barColorPrimary': {
                backgroundColor: ACCENT,
            },
        },
        analysisBlock: {
            padding: theme.spacing(1.5, 0),
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:last-child': {
                borderBottom: 'none',
            },
            // Explicit bug report: emphasis cards rendered flush against each other with no
            // visible border/gap between them. Root cause - analysisBlock and
            // assessmentSectionEmphasis are combined as two plain classes on the same element,
            // and both set padding/margin/border; which one's values actually win is down to
            // JSS's own rule-insertion order, not anything declared here (same class of bug as
            // App.css's own rv-header-action-hover comment). JSS's `&$ruleName` compound selector
            // instead generates `.analysisBlock.assessmentSectionEmphasis { ... }`, whose two-
            // class specificity always beats the single-class `.analysisBlock` rule above,
            // regardless of insertion order - so this is the only place these values are set.
            '&$assessmentSectionEmphasis': {
                margin: theme.spacing(0.5, 0),
                padding: theme.spacing(1, 1.25),
                borderRadius: 6,
                backgroundColor: '#fff8ec',
                border: `1px solid ${theme.palette.warning.light}`,
            },
        },
        analysisBlockHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(0.5),
        },
        sectionActions: {
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
        },
        assessmentSectionTitle: {
            fontSize: 13,
            fontWeight: 700,
            color: ACCENT,
            // Same &$ruleName reasoning as analysisBlock's own comment above - keeps the
            // warning colour override deterministic regardless of rule-insertion order.
            '&$assessmentSectionEmphasisTitle': {
                color: theme.palette.warning.dark,
            },
        },
        // Explicit request: risks/missing information/urgent dates stand out from the rest of the
        // assessment rather than reading as just another section in the list. Deliberately empty -
        // see analysisBlock's own '&$assessmentSectionEmphasis' nested rule above, which is where
        // the actual combined styling lives.
        assessmentSectionEmphasis: {},
        // Deliberately empty - see assessmentSectionTitle's own '&$assessmentSectionEmphasisTitle'
        // nested rule above.
        assessmentSectionEmphasisTitle: {},
        disclaimerBox: {
            marginTop: theme.spacing(1.5),
            padding: theme.spacing(1, 1.25),
            borderRadius: 6,
            backgroundColor: theme.palette.action.hover,
            fontSize: 11.5,
            fontStyle: 'italic',
            color: theme.palette.text.secondary,
        },
        urgentDateRow: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(1),
            '&:last-child': {
                marginBottom: 0,
            },
        },
        urgentDateBadge: {
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 700,
            color: theme.palette.warning.dark,
            backgroundColor: '#fff',
            border: `1px solid ${theme.palette.warning.light}`,
            borderRadius: 999,
            padding: '2px 9px',
            whiteSpace: 'nowrap',
        },
        urgentDateBody: {
            minWidth: 0,
        },
        sourcesRow: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
        },
        progressSubLine: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            textAlign: 'center',
            marginBottom: 2,
        },
        // Explicit request: a run can take a while, so this nudges the person toward minimizing
        // and coming back later, rather than feeling like they have to sit and watch the stepper.
        minimizeHintBanner: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(2),
            padding: theme.spacing(1, 1.25),
            borderRadius: 6,
            backgroundColor: ACCENT_SOFT,
            color: ACCENT,
            fontSize: 12,
            '& .MuiSvgIcon-root': { fontSize: 17, flexShrink: 0 },
        },
        // Explicit request: the analysis text is already plain, selectable text (no userSelect
        // override anywhere prevents that) - this is a convenience one-click affordance on top of
        // manual select+copy, for pulling a result straight into an enquiry field elsewhere.
        copyBtn: {
            padding: 4,
            flexShrink: 0,
            color: theme.palette.text.disabled,
            '&:hover': {
                color: ACCENT,
                backgroundColor: ACCENT_SOFT,
            },
        },
        copyBtnCopied: {
            color: theme.palette.success.main,
        },
        analysisText: {
            fontSize: 14,
            lineHeight: 1.6,
            color: theme.palette.text.primary,
            whiteSpace: 'pre-wrap',
            userSelect: 'text',
        },
        errorBanner: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1.25),
            padding: theme.spacing(1.5, 2.5),
            backgroundColor: '#fff8f8',
            borderBottom: `1px solid ${theme.palette.error.light}`,
        },
        errorBannerIcon: {
            color: theme.palette.error.main,
            marginTop: 1,
        },
        errorBannerTitle: {
            fontSize: 13,
            fontWeight: 700,
            color: theme.palette.error.main,
        },
        errorBannerHint: {
            fontSize: 12,
            color: theme.palette.text.secondary,
            marginTop: 2,
        },
        errorBannerReference: {
            fontSize: 10.5,
            color: theme.palette.text.disabled,
            marginTop: 4,
        },
        // The single scrollable region for the whole body - the analysis (resultSection) and the
        // chat messages (messagesList) scroll together as one thread, rather than the analysis
        // living in its own separately-scrolled, height-capped pane above a mostly-empty chat
        // strip (explicit bug report - see resultSection's own comment).
        chatArea: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
        },
        messagesList: {
            padding: theme.spacing(2, 2.5),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.25),
        },
        chatEmpty: {
            padding: theme.spacing(2, 0),
            fontSize: 12.5,
            color: theme.palette.text.disabled,
            textAlign: 'center',
        },
        historyLoading: {
            padding: theme.spacing(1, 0),
        },
        messageRow: {
            display: 'flex',
            flexDirection: 'column',
        },
        messageRowUser: {
            alignItems: 'flex-end',
        },
        messageRowAssistant: {
            alignItems: 'flex-start',
        },
        bubble: {
            maxWidth: '80%',
            padding: theme.spacing(1, 1.5),
            borderRadius: 12,
            fontSize: 14,
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            userSelect: 'text',
        },
        bubbleAssistant: {
            backgroundColor: ACCENT_SOFT,
            color: theme.palette.text.primary,
            borderBottomLeftRadius: 4,
        },
        // Overrides MarkdownText's own root sizing/spacing to match this bubble's own compact
        // feel - its block-level p/ul margins are meant for a full-width result card, not a
        // narrow chat bubble (a last paragraph's own trailing margin reads as odd empty space
        // right before the bubble's own bottom padding otherwise).
        bubbleMarkdown: {
            fontSize: 14,
            lineHeight: 1.45,
            '& p:last-child, & ul:last-child': {
                marginBottom: 0,
            },
            // Explicit bug report: MarkdownText styles **bold** as a light-blue pill (meant for
            // short named entities on a neutral card), but this bubble's own background is the
            // same AI_ACCENT_SOFT - the pill was invisible, leaving only its 7px side padding as
            // odd gaps before punctuation, and nowrap forced whole bolded clauses onto one
            // unbreakable line. In the bubble, bold is plain heavier accent-coloured text that
            // wraps normally. '&&' doubles the class so this wins over MarkdownText's own
            // '& strong' regardless of stylesheet injection order.
            '&& strong': {
                backgroundColor: 'transparent',
                borderRadius: 0,
                padding: 0,
                whiteSpace: 'normal',
            },
        },
        bubbleAssistantError: {
            backgroundColor: '#fff8f8',
            color: theme.palette.error.main,
        },
        bubbleUser: {
            backgroundColor: ACCENT,
            color: '#fff',
            borderBottomRightRadius: 4,
        },
        typingBubble: {
            display: 'flex',
            alignItems: 'center',
            gap: 4,
        },
        typingDot: {
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: ACCENT,
            animation: '$aiTypingBounce 1.2s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                opacity: 0.6,
            },
        },
        typingDot2: {
            animationDelay: '0.15s',
        },
        typingDot3: {
            animationDelay: '0.3s',
        },
        citationsRow: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            marginTop: 4,
            maxWidth: '80%',
        },
        citationChip: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            fontSize: 10.5,
            color: theme.palette.text.secondary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 999,
            padding: '2px 8px',
            cursor: 'default',
            '& .MuiSvgIcon-root': { fontSize: 11 },
        },
        footer: {
            borderTop: '1px solid #e0e0e0',
            padding: theme.spacing(1.5, 2),
            background: '#fff',
            display: 'flex',
            alignItems: 'flex-end',
            gap: theme.spacing(1),
            flexShrink: 0,
        },
        sendBtn: {
            color: ACCENT,
            '&.Mui-disabled': {
                color: theme.palette.action.disabled,
            },
        },
    })
);

// Reuses the existing /enquiriesSummary/:id route (AppContent.tsx) - EnquirySummaryPage.tsx
// already reacts to props.match.params.id via its own useEffect to auto-open that enquiry's
// EnquiryFormDrawer, and (unlike its sibling ?enquiryGuid= query-param handling, which only
// runs once on initial mount) that effect re-fires on every navigation here even while already
// on the page, which is what repeated "jump to enquiry" clicks need.
const navigateToEnquiry = (enquiryGuid: string) => {
    browserHistory.push(`${linkToPage('enquiriesSummary')}/${encodeURIComponent(enquiryGuid)}`);
};

// Explicit request: "contact support" should open the app's own support dialog with the
// technical detail already filled in, rather than making the person retype a GUID/error code by
// hand (or worse, us showing that raw detail directly in the UI as the headline - see
// AiDocumentChatContext.tsx's own isCustomerSafeFailureText guard for that half of this fix).
const buildSupportNote = (session: DocumentAiSession): string => (
    [
        'AI assessment failed.',
        `Enquiry: ${session.enquiryGuid}`,
        `Error code: ${session.errorCode ?? 'unknown'}`,
        `Message shown: ${session.errorMessage ?? 'unknown'}`,
        `Generation/revision: ${session.generation}/${session.revision}`,
    ].join('\n')
);

interface AiChatDialogProps {
    session: DocumentAiSession;
}

export const AiChatDialog: React.FC<AiChatDialogProps> = ({ session }) => {
    const classes = useStyles();
    const { minimizeSession, closeSession, sendMessage, startProcessing, ensureChatHistoryLoaded, isAssessmentChecking, isChatHistoryLoading } = useAiDocumentChat();
    const [draftMessage, setDraftMessage] = React.useState('');
    const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
    // Explicit request: a "speak" action next to each section's copy button, reading it aloud via
    // the browser's own speech synthesis - audio plays through whatever output device (including
    // connected headphones) is currently active, same as any other sound the browser makes, with
    // no device-detection code needed here.
    const [speakingKey, setSpeakingKey] = React.useState<string | null>(null);
    const chatEndRef = React.useRef<HTMLDivElement>(null);
    // react-draggable's own drag target - nodeRef avoids its default findDOMNode usage, which is
    // deprecated under React's strict mode.
    const panelNodeRef = React.useRef<HTMLDivElement>(null);
    const displayState = getDocumentAiDisplayState(session);
    // documentAIQuestion searches "current, successfully processed attachments" - that's
    // hasResults, not isTerminal, so chat stays available through a reassessment too (against
    // the previous result, same as the analysis panel above it during processingWithResults).
    const canChat = session.hasResults;

    const parsedAssessment = React.useMemo(() => parseAssessmentJson(session.assessmentJson), [session.assessmentJson]);
    const parsedCitations = React.useMemo(() => parseCitationsJson(session.citationsJson), [session.citationsJson]);
    const parsedUrgentDates: unknown[] = parsedAssessment && Array.isArray(parsedAssessment.urgentDates) ? parsedAssessment.urgentDates : [];
    const hasAnyAssessmentContent = parsedAssessment != null
        && (ASSESSMENT_SECTIONS.some(section => !isSectionEmpty(parsedAssessment[section.key])) || parsedUrgentDates.length > 0);

    // Ticks every 20s while processing, purely so the elapsed-time readout below keeps advancing
    // even when the backend goes quiet for a while (e.g. the final combined call) - without this,
    // nothing would re-render to update it until the next actual progress event arrives, which is
    // exactly the scenario this readout exists to reassure the user through.
    const [nowTick, setNowTick] = React.useState(() => Date.now());
    React.useEffect(() => {
        if (displayState !== 'processing' && displayState !== 'processingWithResults') {
            return;
        }
        const interval = setInterval(() => setNowTick(Date.now()), 20000);
        return () => clearInterval(interval);
    }, [displayState]);

    React.useEffect(() => {
        ensureChatHistoryLoaded(session.key, session.enquiryGuid);
    // tslint:disable-next-line: align
    }, [session.key, session.enquiryGuid, ensureChatHistoryLoaded]);

    React.useEffect(() => {
        chatEndRef.current?.scrollIntoView({ block: 'end' });
    }, [session.messages.length, session.isSendingMessage]);

    const onSend = () => {
        if (!draftMessage.trim() || !canChat) {
            return;
        }
        sendMessage(session.key, draftMessage);
        setDraftMessage('');
    };

    // The explicit in-dialog "Reassess" button - always a full reassessEnquiryAI, never
    // conditional on anything (unlike the header badge's own click, which goes through
    // requestAssessment instead).
    const onReassessClick = () => {
        startProcessing(session.enquiryGuid, session.title, session.enquirySummary, true);
    };

    // A failed run's own retry - replays the SAME kind of mutation that actually failed
    // (session.lastDispatchWasReassess), rather than always defaulting to one or the other:
    // retry a failed full reassessment with reassessEnquiryAI, retry a failed incremental assess
    // with assessEnquiryAI.
    const onRetryClick = () => {
        startProcessing(session.enquiryGuid, session.title, session.enquirySummary, session.lastDispatchWasReassess);
    };

    const onCopy = (copyKey: string, text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopiedKey(copyKey);
                setTimeout(() => setCopiedKey(current => (current === copyKey ? null : current)), 1500);
            })
            // tslint:disable-next-line: no-empty
            .catch(() => {});
    };

    // Toggle - clicking the same section's speak button again stops it, matching the icon flip
    // (speaker -> stop) rather than queueing a second utterance on top. Strips the same **/*/_
    // emphasis markers and leading bullet dashes a reader wouldn't want read aloud literally.
    const onSpeak = (speakKey: string, rawText: string) => {
        if (typeof window === 'undefined' || !window.speechSynthesis) {
            return;
        }
        if (speakingKey === speakKey) {
            window.speechSynthesis.cancel();
            setSpeakingKey(null);
            return;
        }
        window.speechSynthesis.cancel();
        const spokenText = stripMarkdownEmphasis(rawText)
            .split('\n')
            .map(line => line.replace(/^[-*]\s+/, ''))
            .join('. ');
        const utterance = new SpeechSynthesisUtterance(spokenText);
        utterance.onend = () => setSpeakingKey(current => (current === speakKey ? null : current));
        utterance.onerror = () => setSpeakingKey(current => (current === speakKey ? null : current));
        setSpeakingKey(speakKey);
        window.speechSynthesis.speak(utterance);
    };

    // Stops any in-progress speech the moment this dialog itself unmounts (closed), rather than
    // leaving it talking over whatever the person does next.
    React.useEffect(() => () => {
        if (typeof window !== 'undefined' && window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
    // tslint:disable-next-line: align
    }, []);

    // Non-modal and portalled straight to document.body (bypassing MUI's Dialog/Modal
    // entirely - see AI_PANEL_Z_INDEX's own comment above for why), per explicit request: the
    // person can keep working in the Enquiry drawer (copy from the AI analysis into a field, or
    // check a field while chatting) at the same time as this panel, and dragging it by its own
    // header bar (handle) lets them move it clear of whatever part of the drawer they need.
    return ReactDOM.createPortal(
        <Draggable nodeRef={panelNodeRef} handle="#ai-chat-dialog-header" cancel="#ai-chat-dialog-header-actions">
            <Paper ref={panelNodeRef} elevation={8} className={classes.panel}>
            <Box className={classes.header} id="ai-chat-dialog-header">
                <Box className={classes.headerLeft}>
                    <Box className={classes.headerIconChip}>
                        <WandIcon fontSize="small" />
                    </Box>
                    <Box className={classes.headerText}>
                        <Typography className={classes.headerEyebrow}>Document AI</Typography>
                        <Typography className={classes.headerTitle} title={session.title}>{session.title}</Typography>
                    </Box>
                </Box>
                <Box className={classes.headerActions} id="ai-chat-dialog-header-actions">
                    <Button
                        variant="outlined"
                        size="small"
                        className={classes.openEnquiryBtn}
                        startIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() => navigateToEnquiry(session.enquiryGuid)}
                    >
                        Open enquiry
                    </Button>
                    {/* The explicit, deliberate "Reassess" action - always reassessEnquiryAI,
                        never the error banner's own failed-run retry (onRetryClick, which replays
                        whichever kind actually failed). Hidden while a fresh (never-before-ready)
                        run is still in progress - nothing to reassess yet. Disabled while any run
                        is in flight, since a reassessment re-reads and reprocesses every attached
                        document and bills AI processing costs again - not something to let fire
                        a second time on top of an already-running operation. */}
                    {session.hasResults && (
                        <Tooltip title={session.isTerminal
                            ? 'Reassess this enquiry - re-reads every document and re-runs AI processing costs'
                            : 'A run is already in progress'}
                        >
                            <IconButton size="small" className={classes.headerIconBtn} onClick={onReassessClick} disabled={!session.isTerminal}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Minimize">
                        <IconButton size="small" className={classes.headerIconBtn} onClick={() => minimizeSession(session.key)}>
                            <MinimizeIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton size="small" className={classes.headerIconBtn} onClick={() => closeSession(session.key)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box className={classes.identityRow}>
                <Box className={classes.identityItem}>
                    <PersonOutlineIcon />
                    <span title={session.enquirySummary.name}>{session.enquirySummary.name}</span>
                </Box>
                {session.enquirySummary.email && (
                    <Box className={classes.identityItem}>
                        <MailOutlineIcon />
                        <span title={session.enquirySummary.email}>{session.enquirySummary.email}</span>
                    </Box>
                )}
                {session.enquirySummary.areaOfLaw && (
                    <Box className={classes.identityItem}>
                        <GavelIcon />
                        <span title={session.enquirySummary.areaOfLaw}>{session.enquirySummary.areaOfLaw}</span>
                    </Box>
                )}
            </Box>

            <DialogContent dividers={true} className={classes.body}>
                {displayState === 'failed' && (
                    <Box className={classes.errorBanner}>
                        <ErrorOutlineIcon fontSize="small" className={classes.errorBannerIcon} />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            <Typography className={classes.errorBannerTitle}>
                                {session.errorMessage || 'Something went wrong.'}
                            </Typography>
                            <Typography className={classes.errorBannerHint}>
                                Please try again. If this keeps happening,{' '}
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    onClick={() => requestSupportWithNote(buildSupportNote(session))}
                                >
                                    contact support
                                </Link>.
                            </Typography>
                            {session.errorCode && (
                                <Typography className={classes.errorBannerReference}>
                                    Reference: {session.errorCode}
                                </Typography>
                            )}
                        </Box>
                        <Button size="small" variant="outlined" color="secondary" onClick={onRetryClick}>
                            Try again
                        </Button>
                    </Box>
                )}

                <Box className={classes.chatArea}>
                    <Box className={classes.resultSection}>
                        <Typography className={classes.resultSectionTitle}>AI analysis</Typography>
                        {(displayState === 'processing' || displayState === 'processingWithResults') && (() => {
                            const activeStepIndex = Math.max(0, AI_PROCESSING_STEPS.findIndex(step => step.status === session.processingStatus));

                            // documentsExtracting/Analysing/Reviewing describe simultaneous work
                            // across attachments, not a sequence - shown as one compact line, only
                            // the counts that are actually non-zero.
                            const simultaneousWork = [
                                session.documentsExtracting ? `Extracting: ${session.documentsExtracting}` : null,
                                session.documentsAnalysing ? `Analysing: ${session.documentsAnalysing}` : null,
                                session.documentsReviewing ? `Reviewing: ${session.documentsReviewing}` : null,
                            ].filter((part): part is string => part != null).join(' · ');

                            // Completed counts include failed terminal documents - documentsFailed
                            // is surfaced separately rather than folded into "completed" silently.
                            // Only shown for more than one document - with just one, "0 of 1
                            // documents processed" is trivially obvious and only duplicates the
                            // backend's own progressMessage (which already narrates that single
                            // document's own progress), rather than adding a genuinely different
                            // aggregate view the way it does across a real batch.
                            const documentCountLine = session.documentsTotal != null && session.documentsTotal > 1
                                ? `${session.documentsCompleted ?? 0} of ${session.documentsTotal} documents processed`
                                    + (session.documentsFailed ? `, ${session.documentsFailed} failed` : '')
                                : null;

                            // passagesTotal stays null until every attachment has a known passage
                            // count - never substituted with 0 or a guess, so only the completed
                            // count shows until a real total is known.
                            const passageCountLine = session.passagesCompleted != null
                                ? (session.passagesTotal != null
                                    ? `${session.passagesCompleted} of ${session.passagesTotal} passages analysed`
                                    : `${session.passagesCompleted} passages analysed so far`)
                                : null;

                            // lastProgressAtUtc is the last committed work/stage transition, not a
                            // heartbeat - ticking nowTick (see this component's own effect) keeps
                            // this advancing even while the backend is otherwise quiet, which is
                            // exactly when this reassurance line matters most (in particular, the
                            // final combined call parks at 85% with no finer-grained progress).
                            const elapsedBaseIso = session.lastProgressAtUtc ?? session.createdAtUtc;
                            const elapsedMinutes = session.processingStatus === EnquiryAssessmentProcessingStatus.Assessing && elapsedBaseIso
                                ? Math.max(0, Math.round((nowTick - new Date(elapsedBaseIso).getTime()) / 60000))
                                : null;

                            return (
                                <>
                                    <Box className={classes.minimizeHintBanner}>
                                        <InfoOutlinedIcon />
                                        <span>You can minimize this and carry on with other work - we'll let you know here once it's done.</span>
                                    </Box>
                                    {displayState === 'processingWithResults' && (
                                        <Typography className={classes.progressStage}>Reassessing…</Typography>
                                    )}
                                    <Stepper
                                        activeStep={activeStepIndex}
                                        alternativeLabel={true}
                                        className={classes.stageStepper}
                                    >
                                        {AI_PROCESSING_STEPS.map(step => (
                                            <Step key={step.status}>
                                                <Tooltip title={step.description} arrow={true} placement="top">
                                                    <StepLabel>{step.label}</StepLabel>
                                                </Tooltip>
                                            </Step>
                                        ))}
                                    </Stepper>
                                    {/* The backend's own composed progress text is the primary
                                        line - everything else here is a secondary/supplementary
                                        readout, never a substitute for it. */}
                                    {session.progressMessage && (
                                        <Typography className={classes.stageDetail} align="center">{session.progressMessage}</Typography>
                                    )}
                                    {documentCountLine && (
                                        <Typography className={classes.progressSubLine}>{documentCountLine}</Typography>
                                    )}
                                    {passageCountLine && (
                                        <Typography className={classes.progressSubLine}>{passageCountLine}</Typography>
                                    )}
                                    {simultaneousWork && (
                                        <Typography className={classes.progressSubLine}>{simultaneousWork}</Typography>
                                    )}
                                    {/* isProgressIndeterminate means progressPercent is a coarse
                                        milestone, not measured completion (the final combined call
                                        parks at 85% until it finishes) - an indeterminate bar avoids
                                        implying the number itself is still advancing. */}
                                    {session.isProgressIndeterminate ? (
                                        <LinearProgress variant="indeterminate" className={classes.progressBar} />
                                    ) : session.progressPercent != null && (
                                        <>
                                            <LinearProgress
                                                variant="determinate"
                                                value={session.progressPercent}
                                                className={classes.progressBar}
                                            />
                                            <Typography className={classes.progressStage} align="center">{session.progressPercent}% complete</Typography>
                                        </>
                                    )}
                                    {elapsedMinutes != null && elapsedMinutes >= 1 && (
                                        <Typography className={classes.progressSubLine}>
                                            Waiting for the AI assessment — {elapsedMinutes}m elapsed in this stage
                                        </Typography>
                                    )}
                                    {/* Explicit request: the previous result used to stay visible
                                        underneath the progress UI during a reassessment, which read
                                        as stale leftover content - now hidden (in favour of this
                                        skeleton) for both a fresh run and a reassessment alike. */}
                                    {ANALYSIS_SKELETON_GROUPS.map((group, groupIndex) => (
                                        <Box key={groupIndex} className={classes.analysisSkeletonGroup}>
                                            <SkeletonLoader
                                                variant="text"
                                                rows={1}
                                                widths={[group.headingWidth]}
                                                heights={[18]}
                                                className={classes.analysisSkeletonHeading}
                                            />
                                            <SkeletonLoader variant="text" rows={group.bodyRows} />
                                        </Box>
                                    ))}
                                </>
                            );
                        })()}
                        {/* Explicit bug report: "No analysis is available yet" flashed for a
                            second or two right as a run finished. The terminal event flips
                            isTerminal/hasResults to ready immediately, but assessmentJson itself
                            only lands once the reload that same event triggers resolves -
                            isAssessmentChecking covers exactly that gap (see
                            AiDocumentChatContext.tsx's own comment where it's set), so this only
                            shows the "nothing's there" message once that reload has genuinely
                            settled with nothing to show, not while it's still in flight. */}
                        {displayState === 'ready' && !hasAnyAssessmentContent && isAssessmentChecking(session.enquiryGuid) && (
                            ANALYSIS_SKELETON_GROUPS.map((group, groupIndex) => (
                                <Box key={groupIndex} className={classes.analysisSkeletonGroup}>
                                    <SkeletonLoader
                                        variant="text"
                                        rows={1}
                                        widths={[group.headingWidth]}
                                        heights={[18]}
                                        className={classes.analysisSkeletonHeading}
                                    />
                                    <SkeletonLoader variant="text" rows={group.bodyRows} />
                                </Box>
                            ))
                        )}
                        {displayState === 'ready' && !hasAnyAssessmentContent && !isAssessmentChecking(session.enquiryGuid) && (
                            <Typography variant="body2" color="textSecondary">No analysis is available yet.</Typography>
                        )}
                        {displayState === 'ready' && parsedAssessment && ASSESSMENT_SECTIONS.map((section, sectionIndex) => {
                            const value = parsedAssessment[section.key];
                            if (isSectionEmpty(value)) {
                                return null;
                            }
                            const sectionText = stringifySectionValue(value);
                            return (
                                <AnimatedItem key={section.key} noFill={true} delay={sectionIndex * 40}>
                                    <Box className={`${classes.analysisBlock} ${section.emphasis ? classes.assessmentSectionEmphasis : ''}`}>
                                        <Box className={classes.analysisBlockHeader}>
                                            <Typography className={`${classes.assessmentSectionTitle} ${section.emphasis ? classes.assessmentSectionEmphasisTitle : ''}`}>
                                                {section.label}
                                            </Typography>
                                            <Box className={classes.sectionActions}>
                                                <Tooltip title={speakingKey === section.key ? 'Stop' : 'Read aloud'}>
                                                    <IconButton
                                                        size="small"
                                                        className={`${classes.copyBtn} ${speakingKey === section.key ? classes.copyBtnCopied : ''}`}
                                                        onClick={() => onSpeak(section.key, sectionText)}
                                                    >
                                                        {speakingKey === section.key ? <StopIcon fontSize="small" /> : <VolumeUpOutlinedIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={copiedKey === section.key ? 'Copied' : 'Copy section'}>
                                                    <IconButton
                                                        size="small"
                                                        className={`${classes.copyBtn} ${copiedKey === section.key ? classes.copyBtnCopied : ''}`}
                                                        onClick={() => onCopy(section.key, sectionText)}
                                                    >
                                                        {copiedKey === section.key ? <CheckIcon fontSize="small" /> : <FileCopyOutlinedIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                        <MarkdownText text={sectionText} className={`${classes.analysisText} user-select-text`} />
                                    </Box>
                                </AnimatedItem>
                            );
                        })}
                        {/* Structured {date, event, support} entries, not a narrative string like
                            the sections above - a date badge + description + source line each,
                            not a generic object-to-text dump. */}
                        {displayState === 'ready' && parsedUrgentDates.length > 0 && (
                            <AnimatedItem noFill={true}>
                                <Box className={`${classes.analysisBlock} ${classes.assessmentSectionEmphasis}`}>
                                    <Box className={classes.analysisBlockHeader}>
                                        <Typography className={`${classes.assessmentSectionTitle} ${classes.assessmentSectionEmphasisTitle}`}>
                                            Urgent dates
                                        </Typography>
                                    </Box>
                                    {parsedUrgentDates.map((entry, entryIndex) => {
                                        if (!entry || typeof entry !== 'object') {
                                            return (
                                                <MarkdownText
                                                    key={entryIndex}
                                                    text={stringifySectionValue(entry)}
                                                    className={`${classes.analysisText} user-select-text`}
                                                />
                                            );
                                        }
                                        const { date, event, support } = entry as ParsedUrgentDate;
                                        return (
                                            <Box key={entryIndex} className={classes.urgentDateRow}>
                                                {date != null && (
                                                    <span className={classes.urgentDateBadge}>{stripMarkdownEmphasis(date)}</span>
                                                )}
                                                <Box className={classes.urgentDateBody}>
                                                    {event != null && (
                                                        <MarkdownText
                                                            text={stringifySectionValue(event)}
                                                            className={`${classes.analysisText} user-select-text`}
                                                        />
                                                    )}
                                                    {support != null && (
                                                        <Typography className={`${classes.stageDetail} user-select-text`}>
                                                            Source: {renderInline(stringifySectionValue(support), `urgent-date-${entryIndex}-support`)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </AnimatedItem>
                        )}
                        {/* Backend-resolved citations (citationsJson), not the model's own raw
                            "citations" key inside assessmentJson (deliberately not rendered - see
                            ASSESSMENT_SECTIONS' own comment) - documentName/sourceReference only,
                            GUIDs kept only as React keys, never shown. */}
                        {displayState === 'ready' && parsedCitations.length > 0 && (
                            <AnimatedItem noFill={true}>
                                <Box className={classes.analysisBlock}>
                                    <Typography className={classes.assessmentSectionTitle}>Sources</Typography>
                                    <Box className={classes.sourcesRow}>
                                        {parsedCitations.map((citation, citationIndex) => {
                                            const documentName = typeof citation.documentName === 'string' && citation.documentName.trim()
                                                ? citation.documentName
                                                : 'Document';
                                            const sourceReference = typeof citation.sourceReference === 'string' && citation.sourceReference.trim()
                                                ? citation.sourceReference
                                                : null;
                                            const citationKey = typeof citation.documentGuid === 'string' ? citation.documentGuid : citationIndex;
                                            return (
                                                <span key={citationKey} className={classes.citationChip}>
                                                    <FormatQuoteIcon />
                                                    {documentName}{sourceReference ? `, ${sourceReference}` : ''}
                                                </span>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </AnimatedItem>
                        )}
                        {displayState === 'ready' && parsedAssessment && typeof parsedAssessment.disclaimer === 'string' && parsedAssessment.disclaimer.trim().length > 0 && (
                            <Typography className={`${classes.disclaimerBox} user-select-text`}>
                                {renderInline(parsedAssessment.disclaimer, 'disclaimer')}
                            </Typography>
                        )}
                    </Box>

                    <Box className={classes.messagesList}>
                        {/* Explicit bug report: the saved conversation "just appeared suddenly" -
                            documentAIInteractions loads asynchronously (see ensureChatHistoryLoaded
                            in AiDocumentChatContext.tsx), and with nothing shown in the meantime a
                            genuinely empty history and one still loading looked identical. */}
                        {isChatHistoryLoading(session.key) ? (
                            <Box className={classes.historyLoading}>
                                <SkeletonLoader variant="panel" columns={1} rows={3} />
                                <Typography className={classes.chatEmpty}>Loading conversation…</Typography>
                            </Box>
                        ) : session.messages.length === 0 && (
                            <Typography className={classes.chatEmpty}>
                                {canChat ? "Ask a question about this enquiry's documents." : 'You can ask questions once processing finishes.'}
                            </Typography>
                        )}
                        {session.messages.map((chatMessage, index) => (
                            <AnimatedItem key={chatMessage.id} noFill={true}>
                                <Box
                                    className={`${classes.messageRow} ${chatMessage.role === 'user' ? classes.messageRowUser : classes.messageRowAssistant}`}
                                >
                                    <Box className={`${classes.bubble} user-select-text ${chatMessage.role === 'user' ? classes.bubbleUser : classes.bubbleAssistant} ${chatMessage.isError ? classes.bubbleAssistantError : ''}`}>
                                        {/* documentAIQuestion's answer uses the same constrained
                                            Markdown as the saved assessment/analysis text - only
                                            parse it for the assistant's own replies, never the
                                            user's own typed question (which should render exactly
                                            as typed, including any literal asterisks). */}
                                        {chatMessage.role === 'assistant'
                                            ? <MarkdownText text={chatMessage.content} className={classes.bubbleMarkdown} />
                                            : chatMessage.content}
                                    </Box>
                                    {chatMessage.role === 'user' && chatMessage.sendFailed && (
                                        <Typography className={classes.stageDetail}>
                                            Couldn't send.{' '}
                                            <Button size="small" onClick={() => sendMessage(session.key, chatMessage.content, chatMessage.operationGuid)}>
                                                Retry
                                            </Button>
                                        </Typography>
                                    )}
                                    {chatMessage.role === 'assistant' && chatMessage.isError && (
                                        <Typography className={classes.stageDetail}>
                                            <Button size="small" onClick={() => sendMessage(session.key, session.messages[index - 1]?.content ?? '')}>
                                                Retry
                                            </Button>
                                        </Typography>
                                    )}
                                    {chatMessage.citations && chatMessage.citations.length > 0 && (
                                        <Box className={classes.citationsRow}>
                                            {chatMessage.citations.map((citation, citationIndex) => {
                                                const pageRange = citation.startPageNumber
                                                    ? (citation.endPageNumber && citation.endPageNumber !== citation.startPageNumber
                                                        ? `p.${citation.startPageNumber}-${citation.endPageNumber}`
                                                        : `p.${citation.startPageNumber}`)
                                                    : null;
                                                return (
                                                    <Tooltip key={`${citation.documentGuid}-${citationIndex}`} title={citation.excerpt} placement="top" arrow={true}>
                                                        <span className={classes.citationChip}>
                                                            <FormatQuoteIcon />
                                                            {citation.documentName}{pageRange ? `, ${pageRange}` : ''}
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            </AnimatedItem>
                        ))}
                        {session.isSendingMessage && (
                            <AnimatedItem noFill={true}>
                                <Box className={`${classes.messageRow} ${classes.messageRowAssistant}`}>
                                    <Box className={`${classes.bubble} ${classes.bubbleAssistant} ${classes.typingBubble}`}>
                                        <span className={classes.typingDot} />
                                        <span className={`${classes.typingDot} ${classes.typingDot2}`} />
                                        <span className={`${classes.typingDot} ${classes.typingDot3}`} />
                                    </Box>
                                </Box>
                            </AnimatedItem>
                        )}
                        <div ref={chatEndRef} />
                    </Box>
                </Box>
            </DialogContent>

            <Box className={classes.footer}>
                <TextField
                    variant="outlined"
                    fullWidth={true}
                    size="small"
                    placeholder={canChat ? "Ask a question about this enquiry's documents..." : 'Waiting for processing to finish...'}
                    value={draftMessage}
                    disabled={!canChat}
                    onChange={e => setDraftMessage(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onSend();
                        }
                    }}
                    multiline={true}
                    maxRows={4}
                />
                <IconButton
                    className={classes.sendBtn}
                    disabled={!canChat || !draftMessage.trim()}
                    onClick={onSend}
                >
                    <SendIcon />
                </IconButton>
            </Box>
            </Paper>
        </Draggable>,
        document.body,
    );
};
